import React, { useContext } from "react";
import { DialogContext } from "../dialogs/DialogHelper";
import { APColumn, APRow } from "../layout/APFlex";
import { APScrollView } from "../layout/APScrollView";
import { APExpanded, APSizedBox } from "../layout/Common";
import { APCard } from "./APCard";
import { APText } from "./APText";
import { APPalette } from "../utils";
import { CloseIcon } from "../icons";
import APIconButton from "../icons/APIconButton";

export interface APDialogCardProps {
    id?: string;
    style?: React.CSSProperties
    /**
     * title of the dialog
     */
    title: string
    description?: string
    children: React.ReactNode
    /**
     * adds any node before the dialog title
     * @private used in confirmation and error dialog
     */
    leading?: React.ReactNode
    /**
     * call to actions which will appear at the bottom of the dialog
     */
    actions?: React.ReactNode
    /**
     * adds any node after the dialog title
     */
    leftFloat?: React.ReactNode;
    trailing?: JSX.Element;
    /**
     * Hides the default header row (title + close button).
     * Use this when you need a custom header inside children.
     */
    hideHeader?: boolean;
    /**
     * @deprecated
     */
    contentDivider?: boolean;
    /**
     * @deprecated
     */
    contentDividerOffset?: string;
}

export function APDialogCard({
    id,
    style,
    title,
    description,
    leading,
    actions,
    trailing,
    children,
    leftFloat,
    contentDivider = false,
    contentDividerOffset,
    hideHeader = false,
}: APDialogCardProps) {

    const dialogContext = useContext(DialogContext);

    return (
        <APCard id={id} style={{ padding: "16px", minWidth: "420px", maxWidth: "90vw", ...style, boxSizing: "border-box", borderRadius: "8px" }}>
            <APRow crossAxisAlignment="stretch" mainAxisSize="max" gap="16px">
                {
                    leftFloat && <div>{leftFloat}</div>
                }
                <APColumn mainAxisSize="max" crossAxisAlignment="stretch">
                    {/* Card Header */}
                    {!hideHeader && (
                        <APRow gap='8px' mainAxisAlignment='spaceBetween'>
                            {leading}
                            <APExpanded>
                                <APText variant='h3' color={APPalette['grey-700']} ellipsis> {title}</APText>
                            </APExpanded>
                            {trailing}
                            {
                                (!dialogContext.disableClose) &&
                                <APIconButton onClick={() => dialogContext.deferred?.reject(new Error("USER_CANCELLED"))}>
                                    <CloseIcon />
                                </APIconButton>
                            }
                        </APRow>)
                    }
                    {
                        description &&
                        <>
                            <APSizedBox height="4px" />
                            <APText variant="paragraph-small" color={APPalette['grey-600']}>{description}</APText>
                        </>
                    }
                    {/* Card Body */}
                    {!hideHeader && <APSizedBox height="8px" />}
                    <APExpanded>
                        <APScrollView>
                            {children}
                        </APScrollView>
                    </APExpanded>
                    {/* Card Actions */}
                    {
                        actions &&
                        <>
                            <APSizedBox height="8px" />
                            {actions}
                        </>
                    }
                </APColumn>
            </APRow>
        </APCard>
    )
}
